/**
 * Status vocabulary.
 *
 * Values are exactly the backend's. Labels are the product's. Every entry pairs a
 * colour with an icon and text, because colour alone is not an accessible signal —
 * and these screens get read on projectors in site offices.
 */

import {
  AlertTriangle,
  Archive,
  Ban,
  CheckCircle2,
  CircleDot,
  Clock,
  Inbox,
  Minus,
  PenLine,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
  Wrench,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { InspectionStatus, IssueStatus, Priority } from '@/types/api'

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'violet'

export interface StatusMeta {
  label: string
  tone: Tone
  icon: LucideIcon
  description: string
}

export const INSPECTION_STATUS: Record<InspectionStatus, StatusMeta> = {
  awaiting_safety_ack: {
    label: 'Awaiting check-in',
    tone: 'neutral',
    icon: Clock,
    description: 'Created, but the inspector has not acknowledged the site safety check yet.',
  },
  in_progress: {
    label: 'In progress',
    tone: 'info',
    icon: PlayCircle,
    description: 'The inspector is working through the checklist.',
  },
  awaiting_signoff: {
    label: 'Awaiting sign-off',
    tone: 'warning',
    icon: PenLine,
    description: 'All checks are actioned. Waiting on signatures.',
  },
  submitted: {
    label: 'Submitted',
    tone: 'violet',
    icon: Inbox,
    description: 'Signed and submitted by the inspector. Ready for a report.',
  },
  reported: {
    label: 'Reported',
    tone: 'success',
    icon: CheckCircle2,
    description: 'The handover report has been generated.',
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    icon: Ban,
    description: 'Stopped by an administrator and will not be completed.',
  },
}

/** Lifecycle order — used to sort charts and tabs so the shape stays comparable. */
export const INSPECTION_STATUS_ORDER: InspectionStatus[] = [
  'awaiting_safety_ack',
  'in_progress',
  'awaiting_signoff',
  'submitted',
  'reported',
  'cancelled',
]

/** Statuses an inspector can still work on. Mirrors OPEN_INSPECTION_STATUSES. */
export const OPEN_INSPECTION_STATUSES: InspectionStatus[] = [
  'awaiting_safety_ack',
  'in_progress',
  'awaiting_signoff',
]

export const PRIORITY: Record<Priority, StatusMeta> = {
  high: {
    label: 'High',
    tone: 'danger',
    icon: AlertTriangle,
    description: 'Blocks handover or presents a safety risk.',
  },
  medium: {
    label: 'Medium',
    tone: 'warning',
    icon: CircleDot,
    description: 'Should be fixed before the customer moves in.',
  },
  low: {
    label: 'Low',
    tone: 'neutral',
    icon: Minus,
    description: 'Cosmetic or minor.',
  },
}

export const PRIORITY_ORDER: Priority[] = ['high', 'medium', 'low']

export const ISSUE_STATUS: Record<IssueStatus, StatusMeta> = {
  open: {
    label: 'Open',
    tone: 'danger',
    icon: CircleDot,
    description: 'Raised by the inspector, not yet picked up.',
  },
  acknowledged: {
    label: 'Assigned',
    tone: 'warning',
    icon: Clock,
    description: 'Seen and accepted, work not started.',
  },
  in_progress: {
    label: 'In progress',
    tone: 'info',
    icon: Wrench,
    description: 'A vendor or engineer is fixing it.',
  },
  resolved: {
    label: 'Resolved',
    tone: 'info',
    icon: CheckCircle2,
    description: 'Work is done. Awaiting verification before it can be closed.',
  },
  verified: {
    label: 'Verified',
    tone: 'success',
    icon: ShieldCheck,
    description: 'Checked and confirmed fixed.',
  },
  reopened: {
    label: 'Reopened',
    tone: 'danger',
    icon: RotateCcw,
    description: 'Came back after being resolved or closed.',
  },
  closed: {
    label: 'Closed',
    tone: 'neutral',
    icon: Archive,
    description: 'Verified and signed off. No further work.',
  },
  wont_fix: {
    label: "Won't fix",
    tone: 'neutral',
    icon: Ban,
    description: 'Accepted as-is. No work will be done.',
  },
}

export const ISSUE_STATUS_ORDER: IssueStatus[] = [
  'open',
  'acknowledged',
  'in_progress',
  'resolved',
  'verified',
  'closed',
  'reopened',
  'wont_fix',
]

export const CLOSED_ISSUE_STATUSES: IssueStatus[] = [
  'resolved',
  'verified',
  'closed',
  'wont_fix',
]

export const TEMPLATE_STATUS: Record<string, StatusMeta> = {
  draft: {
    label: 'Draft',
    tone: 'warning',
    icon: PenLine,
    description: 'Editable. Not yet available to inspectors.',
  },
  published: {
    label: 'Published',
    tone: 'success',
    icon: CheckCircle2,
    description: 'Frozen and in use for new inspections.',
  },
  archived: {
    label: 'Archived',
    tone: 'neutral',
    icon: Ban,
    description: 'Retired. Existing inspections keep their pinned version.',
  },
}

export function inspectionStatusMeta(status: string): StatusMeta {
  return (
    INSPECTION_STATUS[status as InspectionStatus] ?? {
      label: status,
      tone: 'neutral' as const,
      icon: CircleDot,
      description: '',
    }
  )
}

export function priorityMeta(priority: string): StatusMeta {
  return PRIORITY[priority as Priority] ?? PRIORITY.low
}

export function issueStatusMeta(status: string): StatusMeta {
  return ISSUE_STATUS[status as IssueStatus] ?? ISSUE_STATUS.open
}

export function templateStatusMeta(status: string): StatusMeta {
  return TEMPLATE_STATUS[status] ?? TEMPLATE_STATUS.draft!
}

/** True when an inspection can still be worked on in the mobile app. */
export function isOpenInspection(status: string): boolean {
  return OPEN_INSPECTION_STATUSES.includes(status as InspectionStatus)
}


/**
 * Legal snag transitions, mirroring ISSUE_TRANSITIONS on the backend.
 *
 * The backend validates these and returns 409 invalid_transition with the allowed
 * targets. This copy exists so the UI can render only legal moves rather than
 * offering one and then explaining the rejection — it is a convenience, never the
 * authority.
 */
export const ISSUE_TRANSITIONS: Record<IssueStatus, IssueStatus[]> = {
  open: ['acknowledged', 'in_progress', 'wont_fix'],
  acknowledged: ['in_progress', 'open', 'wont_fix'],
  in_progress: ['resolved', 'acknowledged', 'wont_fix'],
  resolved: ['verified', 'reopened'],
  verified: ['closed', 'reopened'],
  reopened: ['acknowledged', 'in_progress'],
  wont_fix: ['reopened'],
  closed: ['reopened'],
}

/** Statuses an issue may move to from where it is now, including staying put. */
export function allowedIssueTransitions(current: string): IssueStatus[] {
  const next = ISSUE_TRANSITIONS[current as IssueStatus] ?? []
  return [current as IssueStatus, ...next]
}
